import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  Search,
  Plus,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Eye,
  EyeOff,
  PieChart,
  BarChart3,
  CandlestickChart,
  LineChart,
  Landmark,
  Building,
  Shield,
  Briefcase,
  CircleDollarSign,
  Coins,
  Wallet,
  Home,
  Edit2,
  Trash2,
  Gem,
  Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced, maskCurrency, getCurrencySymbol } from '@/lib/market';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Local icon map for Tailwind/bundler visibility
const ASSET_ICON_MAP = {
  CandlestickChart, PieChart, BarChart3, Landmark, Building, Coins,
  Wallet, Shield, Briefcase, CircleDollarSign, LineChart, Palette, Gem
};

// Fallback asset class list used when fo_data config hasn't been seeded yet
const FALLBACK_ASSET_CLASSES = [
  // ── Public Markets ──────────────────────────────
  { key: 'stocks',             label: 'Equities' },
  { key: 'mf',                 label: 'Mutual Funds' },
  { key: 'etf',                label: 'ETFs' },
  { key: 'bonds',              label: 'Debt / Bonds' },
  { key: 'fd',                 label: 'Fixed Deposits' },
  { key: 'gold',               label: 'Gold / Silver' },
  { key: 'nps',                label: 'National Pension System' },
  { key: 'pms',                label: 'Portfolio Management Services' },
  { key: 'aif',                label: 'Alternative Investment Funds' },
  { key: 'ppf',                label: 'PPF / EPF' },
  { key: 'fo',                 label: 'Futures & Options' },
  // ── Private Markets ─────────────────────────────
  { key: 'vc',                 label: 'Venture Capital' },
  { key: 'pe',                 label: 'Private Equity' },
  { key: 'credit',             label: 'Private Credit' },
  { key: 'infra',              label: 'Infrastructure' },
  { key: 'search',             label: 'Search Funds' },
  { key: 'long_only',          label: 'Long-Only Equity' },
  { key: 'hedge_fund',         label: 'Hedge Funds' },
  { key: 'secondaries',        label: 'Secondaries' },
  { key: 'special_situations', label: 'Special Situations' },
  { key: 'pre_ipo',            label: 'Pre-IPO' },
  { key: 'mezzanine',          label: 'Mezzanine Debt' },
  { key: 'fof',                label: 'Fund of Funds' },
  { key: 'co_investment',      label: 'Co-Investments' },
  { key: 'royalty',            label: 'Royalty / RBF' },
  { key: 'natural_resources',  label: 'Natural Resources' },
  // ── Others ──────────────────────────────────────
  { key: 'realestate',         label: 'Real Estate' },
];

const FALLBACK_LABEL_MAP = Object.fromEntries(FALLBACK_ASSET_CLASSES.map(a => [a.key, a.label]));

const emptyHolding = {
  portfolio_id: '',
  asset_class: 'stocks',
  symbol: '',
  name: '',
  isin: '',
  exchange: 'NSE',
  quantity: '',
  avg_price: '',
  current_price: '',
  broker: '',
  demat_account: '',
  folio_number: '',
  maturity_date: '',
  interest_rate: '',
  face_value: '',
  coupon_rate: '',
  commitment: '',
  called_amount: '',
  nav: '',
};

export default function HoldingsPage() {
  const { user, signOut } = useAuth();
  const { assetClasses, assetClassMap } = useAppData();
  const { assetClass: paramAssetClass } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isStandalone = !!paramAssetClass;
  const navigate = useNavigate();

  // selectedFilter drives the active asset class pill — synced from URL when embedded
  const urlAsset = searchParams.get('asset') || 'all';
  const [selectedFilter, setSelectedFilter] = useState(paramAssetClass || urlAsset);

  // Sync filter from URL route param when navigating between /portfolio/:assetClass paths
  useEffect(() => {
    if (isStandalone && paramAssetClass) setSelectedFilter(paramAssetClass);
  }, [paramAssetClass, isStandalone]);

  // Sync filter from search param when embedded in PublicMarketsDashboard
  useEffect(() => {
    if (!isStandalone) setSelectedFilter(urlAsset);
  }, [urlAsset, isStandalone]);

  const [loading, setLoading] = useState(true);
  const [holdings, setHoldings] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('current_value');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showValues, setShowValues] = useState(true);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentHolding, setCurrentHolding] = useState(emptyHolding);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Auto-open add dialog when ?add=true is in URL
  useEffect(() => {
    if (searchParams.get('add') === 'true' && portfolios.length > 0 && !isDialogOpen) {
      setCurrentHolding({ ...emptyHolding, portfolio_id: portfolios[0]?.id || '', asset_class: urlAsset !== 'all' ? urlAsset : 'stocks' });
      setIsEditing(false);
      setIsDialogOpen(true);
      setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('add'); return n; });
    }
  }, [searchParams, portfolios]); // eslint-disable-line

  const activeFilter = selectedFilter === 'all' ? undefined : selectedFilter;
  const currentAssetRow = assetClassMap[activeFilter] || {};
  const resolveLabel = (key) => currentAssetRow.label || FALLBACK_LABEL_MAP[key] || key;
  const AssetIcon = ASSET_ICON_MAP[currentAssetRow.data?.icon_name] || Wallet;

  // Always fetch all holdings; filter client-side
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [holdingsRes, portfoliosRes] = await Promise.all([
        axios.get(`${API}/markets/holdings`),
        axios.get(`${API}/markets/portfolios`)
      ]);

      setHoldings(holdingsRes.data);
      setPortfolios(portfoliosRes.data || []);

      if (portfoliosRes.data?.length > 0 && !currentHolding.portfolio_id) {
        setCurrentHolding(prev => ({ ...prev, portfolio_id: portfoliosRes.data[0].id }));
      }
    } catch (err) {
      console.error('Error fetching holdings:', err);
      toast.error('Failed to load holdings');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAssetFilter = (ac) => {
    if (isStandalone) {
      if (ac === 'all') navigate('/markets?tab=holdings');
      else navigate(`/portfolio/${ac}`);
    } else {
      setSelectedFilter(ac);
      if (ac === 'all') setSearchParams({ tab: 'holdings' });
      else setSearchParams({ tab: 'holdings', asset: ac });
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => {
    if (!showValues) return maskCurrency(user?.market);
    return formatCurrencySpaced(value, user?.market);
  };
  const currSym = getCurrencySymbol(user?.market);

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2) || 0}%`;
  };

  // Asset classes present in holdings (for filter pills)
  const presentAssetClasses = [...new Set(holdings.map(h => h.asset_class).filter(Boolean))];

  const PUBLIC_MARKETS_EXCLUDED = ['fd', 'cash'];

  // Filter and sort holdings (asset class + search query)
  const filteredHoldings = holdings
    .filter(h => {
      if (!activeFilter && PUBLIC_MARKETS_EXCLUDED.includes(h.asset_class)) return false;
      const matchesAsset = !activeFilter || h.asset_class === activeFilter;
      const matchesSearch =
        h.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        h.name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesAsset && matchesSearch;
    })
    .sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

  // Calculate totals
  const totalValue = filteredHoldings.reduce((sum, h) => sum + (h.current_value || 0), 0);
  const totalInvested = filteredHoldings.reduce((sum, h) => sum + (h.invested_value || 0), 0);
  const totalGain = totalValue - totalInvested;
  const totalGainPercent = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortOrder === 'desc' ? 
      <ChevronDown className="w-4 h-4 text-[#ffd87a]" /> : 
      <ChevronUp className="w-4 h-4 text-[#ffd87a]" />;
  };

  const handleAddNew = () => {
    setCurrentHolding({
      ...emptyHolding,
      portfolio_id: portfolios[0]?.id || '',
      asset_class: activeFilter || 'stocks'
    });
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (holding) => {
    setCurrentHolding({
      ...holding,
      quantity: holding.quantity || '',
      avg_price: holding.avg_price || '',
      current_price: holding.current_price || '',
      interest_rate: holding.interest_rate || '',
      face_value: holding.face_value || '',
      coupon_rate: holding.coupon_rate || '',
      commitment: holding.commitment || '',
      called_amount: holding.called_amount || '',
      nav: holding.nav || '',
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (holdingId) => {
    try {
      await axios.delete(`${API}/markets/holdings/${holdingId}`);
      toast.success('Holding deleted successfully');
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting holding:', error);
      toast.error('Failed to delete holding');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const data = {
        portfolio_id: currentHolding.portfolio_id,
        asset_class: currentHolding.asset_class,
        symbol: currentHolding.symbol,
        name: currentHolding.name,
        isin: currentHolding.isin || null,
        exchange: currentHolding.exchange || null,
        quantity: currentHolding.quantity ? parseFloat(currentHolding.quantity) : 0,
        avg_price: currentHolding.avg_price ? parseFloat(currentHolding.avg_price) : 0,
        current_price: currentHolding.current_price ? parseFloat(currentHolding.current_price) : 0,
        broker: currentHolding.broker || null,
        demat_account: currentHolding.demat_account || null,
        folio_number: currentHolding.folio_number || null,
        maturity_date: currentHolding.maturity_date || null,
        interest_rate: currentHolding.interest_rate ? parseFloat(currentHolding.interest_rate) : null,
        face_value: currentHolding.face_value ? parseFloat(currentHolding.face_value) : null,
        coupon_rate: currentHolding.coupon_rate ? parseFloat(currentHolding.coupon_rate) : null,
        commitment: currentHolding.commitment ? parseFloat(currentHolding.commitment) : null,
        called_amount: currentHolding.called_amount ? parseFloat(currentHolding.called_amount) : null,
        nav: currentHolding.nav ? parseFloat(currentHolding.nav) : null,
      };

      if (isEditing) {
        await axios.put(`${API}/markets/holdings/${currentHolding.id}`, data);
        toast.success('Holding updated successfully');
      } else {
        await axios.post(`${API}/markets/holdings`, data);
        toast.success('Holding added successfully');
      }

      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving holding:', error);
      toast.error(error.response?.data?.detail || 'Failed to save holding');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setCurrentHolding(prev => ({ ...prev, [field]: value }));
  };

  // Per-asset-class field definitions with dynamic labels and placeholders
  const FIELD_CONFIG = {
    stocks:             { fields: ['symbol','name','isin','exchange','quantity','avg_price','current_price','broker','demat_account'],
                          labels: { symbol:'Stock Symbol', name:'Company Name', avg_price:'Avg Buy Price', current_price:'Current Price', broker:'Broker' },
                          placeholders: { symbol:'RELIANCE', name:'Reliance Industries Ltd', avg_price:'2500', current_price:'2800', broker:'Zerodha / ICICI Direct' } },
    etf:                { fields: ['symbol','name','isin','exchange','quantity','avg_price','current_price','broker','demat_account'],
                          labels: { symbol:'ETF Symbol', name:'ETF Name', avg_price:'Avg Buy Price', current_price:'Current Price' },
                          placeholders: { symbol:'NIFTYBEES', name:'Nippon India ETF Nifty 50', avg_price:'250', current_price:'265' } },
    mf:                 { fields: ['symbol','name','isin','folio_number','quantity','avg_price','current_price','broker'],
                          labels: { symbol:'Scheme Code / ISIN', name:'Scheme Name', quantity:'Units Held', avg_price:'Avg NAV at Purchase', current_price:'Current NAV', broker:'Distributor / Platform' },
                          placeholders: { symbol:'INF209K01YG5', name:'Axis Bluechip Fund', quantity:'500.123', avg_price:'45.20', current_price:'52.10', broker:'Groww / MFU' } },
    bonds:              { fields: ['symbol','name','isin','face_value','coupon_rate','quantity','avg_price','maturity_date','broker'],
                          labels: { symbol:'Bond Symbol', name:'Issuer / Bond Name', avg_price:'Purchase Price', face_value:'Face Value', coupon_rate:'Coupon Rate (%)' },
                          placeholders: { symbol:'GSEC2031', name:'7.26% GOI 2032', face_value:'1000', avg_price:'980', coupon_rate:'7.26', broker:'ICICI Securities' } },
    fd:                 { fields: ['name','symbol','avg_price','interest_rate','maturity_date','broker'],
                          labels: { name:'Bank Name', symbol:'FD Reference / Account No.', avg_price:'Principal Amount', interest_rate:'Interest Rate (%)', broker:'Branch / Reference' },
                          placeholders: { name:'HDFC Bank', symbol:'FD/2024/001234', avg_price:'500000', interest_rate:'7.25', broker:'Main Branch' } },
    gold:               { fields: ['symbol','name','quantity','avg_price','current_price','broker'],
                          labels: { symbol:'Type', name:'Description', quantity:'Quantity (grams / units)', avg_price:'Avg Buy Price (per gram/unit)', current_price:'Current Price', broker:'Platform / Custodian' },
                          placeholders: { symbol:'SGB / PHYSICAL / DIGITAL', name:'Sovereign Gold Bond 2026', quantity:'50', avg_price:'5500', current_price:'6200', broker:'Zerodha / Bank' } },
    nps:                { fields: ['symbol','name','avg_price','current_price','interest_rate'],
                          labels: { symbol:'PRAN Number', name:'Fund Manager', avg_price:'Total Invested', current_price:'Current Value', interest_rate:'Annualized Return (%)' },
                          placeholders: { symbol:'110012345678', name:'SBI Pension Fund', avg_price:'250000', current_price:'320000', interest_rate:'10.5' } },
    ppf:                { fields: ['symbol','name','avg_price','current_price','interest_rate'],
                          labels: { symbol:'Account Number', name:'Bank / Post Office', avg_price:'Total Deposited', current_price:'Current Balance', interest_rate:'Interest Rate (%)' },
                          placeholders: { symbol:'PPF/2015/007', name:'SBI / India Post', avg_price:'500000', current_price:'750000', interest_rate:'7.1' } },
    pms:                { fields: ['symbol','name','commitment','called_amount','nav','current_price','broker'],
                          labels: { symbol:'Strategy / Scheme', name:'Portfolio Manager', commitment:'Committed Amount', called_amount:'Deployed Amount', nav:'Latest NAV', current_price:'Current Value', broker:'PMS Provider' },
                          placeholders: { symbol:'VALUE GROWTH', name:'Motilal Oswal PMS', commitment:'5000000', called_amount:'4500000', nav:'125.5', current_price:'5625000' } },
    aif:                { fields: ['symbol','name','commitment','called_amount','nav','current_price','broker'],
                          labels: { symbol:'Fund Name / Class', name:'Fund Manager / GP', commitment:'Commitment Amount', called_amount:'Called Capital', nav:'Latest NAV', current_price:'Current Value', broker:'Fund Manager' },
                          placeholders: { symbol:'CAT III GROWTH', name:'Edelweiss AIF', commitment:'10000000', called_amount:'7000000', nav:'115.2', broker:'Edelweiss' } },
    fo:                 { fields: ['symbol','name','exchange','quantity','avg_price','current_price','broker','demat_account'],
                          labels: { symbol:'Contract Symbol', name:'Contract Description', quantity:'Lots', avg_price:'Entry Price', current_price:'Current Price' },
                          placeholders: { symbol:'NIFTY24DECFUT', name:'NIFTY Dec 24 Futures', quantity:'2', avg_price:'23500', current_price:'24200' } },
    // ── Private Markets ─────────────────────────────
    vc:                 { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund Name', symbol:'Vintage / Series', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current NAV / Value', broker:'GP / Manager' },
                          placeholders: { name:'Sequoia Capital India Fund IV', symbol:'Fund IV', commitment:'20000000', called_amount:'15000000', broker:'Sequoia Capital' } },
    pe:                 { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund Name', symbol:'Vintage / Fund No.', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current NAV', broker:'GP / Manager' },
                          placeholders: { name:'ChrysCapital Fund VIII', symbol:'Fund VIII', commitment:'50000000', called_amount:'40000000', broker:'ChrysCapital' } },
    credit:             { fields: ['name','symbol','commitment','called_amount','avg_price','nav','interest_rate','maturity_date','broker'],
                          labels: { name:'Fund / Instrument Name', symbol:'Tranche / Series', commitment:'Commitment Amount', called_amount:'Deployed Amount', avg_price:'Cost Basis', nav:'Current Value', interest_rate:'Target Yield / IRR (%)', broker:'Manager / Originator' },
                          placeholders: { name:'Edelweiss Private Credit', symbol:'Tranche A', interest_rate:'14.5', broker:'Edelweiss' } },
    infra:              { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund / Project Name', symbol:'Project Code', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current NAV', broker:'Manager / Operator' },
                          placeholders: { name:'India Infrastructure Fund', broker:'IDFC Alternatives' } },
    search:             { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund / Searcher Name', symbol:'Reference', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current Value', broker:'Searcher / Operator' },
                          placeholders: { name:'ABC Search Fund', broker:'John Smith' } },
    long_only:          { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund Name', symbol:'Strategy', commitment:'Commitment Amount', called_amount:'Deployed Amount', avg_price:'Cost Basis', nav:'Current NAV', broker:'Manager' },
                          placeholders: { name:'India Long-Only Equity Fund', broker:'Fund Manager' } },
    hedge_fund:         { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund Name', symbol:'Share Class', commitment:'Subscription Amount', called_amount:'Invested Amount', avg_price:'Avg Buy NAV', nav:'Current NAV', broker:'Prime Broker / Manager' },
                          placeholders: { name:'Global Macro Fund', symbol:'Class A', broker:'Fund Manager' } },
    secondaries:        { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund / Position Name', symbol:'Reference', commitment:'Purchase Price', called_amount:'Deployed', avg_price:'Cost Basis', nav:'Current Value', broker:'Seller / Manager' },
                          placeholders: { name:'Secondaries — Fund ABC', broker:'Secondary Manager' } },
    special_situations: { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Opportunity / Fund Name', symbol:'Reference', commitment:'Commitment Amount', called_amount:'Deployed', avg_price:'Cost Basis', nav:'Current Value', broker:'Manager' },
                          placeholders: { name:'Special Situations Opportunity', broker:'Manager' } },
    pre_ipo:            { fields: ['symbol','name','quantity','avg_price','current_price','broker'],
                          labels: { symbol:'Company Symbol / Ticker', name:'Company Name', quantity:'Shares / Units', avg_price:'Avg Buy Price', current_price:'Current Fair Value', broker:'Intermediary / Platform' },
                          placeholders: { symbol:'COMPANY_PRE', name:'Company Ltd (Pre-IPO)', broker:'InCred / HDFC Securities' } },
    mezzanine:          { fields: ['name','symbol','commitment','called_amount','avg_price','interest_rate','maturity_date','broker'],
                          labels: { name:'Instrument / Company Name', symbol:'Tranche', commitment:'Facility Amount', called_amount:'Drawn Amount', avg_price:'Cost Basis', interest_rate:'Interest / PIK Rate (%)', broker:'Arranger / Manager' },
                          placeholders: { name:'Mezzanine — Company ABC', interest_rate:'16.0', broker:'Arranger' } },
    fof:                { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'FoF Name', symbol:'Vintage', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current NAV', broker:'FoF Manager' },
                          placeholders: { name:'India PE Fund of Funds', broker:'FoF Manager' } },
    co_investment:      { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Company / Deal Name', symbol:'Round / Series', commitment:'Investment Amount', called_amount:'Invested Amount', avg_price:'Cost Per Share', nav:'Current Value', broker:'Lead Investor / GP' },
                          placeholders: { name:'Co-Investment — Company XYZ', symbol:'Series B', broker:'Lead GP' } },
    royalty:            { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Royalty / Agreement Name', symbol:'Reference', commitment:'Capital Deployed', called_amount:'Drawn Amount', avg_price:'Cost Basis', nav:'Current Value', broker:'Counterparty / Manager' },
                          placeholders: { name:'Royalty Stream — Company ABC', broker:'Manager' } },
    natural_resources:  { fields: ['name','symbol','commitment','called_amount','avg_price','nav','broker'],
                          labels: { name:'Fund / Asset Name', symbol:'Asset Type', commitment:'Commitment Amount', called_amount:'Called Capital', avg_price:'Cost Basis', nav:'Current Value', broker:'Manager / Operator' },
                          placeholders: { name:'Natural Resources Fund', symbol:'Timber / Mining / Oil', broker:'Manager' } },
    realestate:         { fields: ['name','symbol','avg_price','current_price','quantity','broker'],
                          labels: { name:'Property Name / Address', symbol:'Property Type', avg_price:'Purchase Price', current_price:'Current Value', quantity:'Area (sq ft)', broker:'Agent / Developer' },
                          placeholders: { name:'204, Green Valley Apartments', symbol:'RESIDENTIAL / COMMERCIAL', avg_price:'8500000', current_price:'10000000', quantity:'1250', broker:'PropTiger / Direct' } },
  };

  const DEFAULT_FIELD_CONFIG = {
    fields: ['symbol','name','quantity','avg_price','current_price','broker'],
    labels: {}, placeholders: {},
  };

  const getFieldsForAssetClass = (ac) => (FIELD_CONFIG[ac] || DEFAULT_FIELD_CONFIG).fields;
  const getLabel = (ac, field, fallback) => FIELD_CONFIG[ac]?.labels?.[field] || fallback;
  const getPlaceholder = (ac, field, fallback) => FIELD_CONFIG[ac]?.placeholders?.[field] || fallback;

  const visibleFields = getFieldsForAssetClass(currentHolding.asset_class);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading holdings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={isStandalone ? 'p-6' : ''} data-testid="holdings-page">
      {/* Back button — only when accessed as standalone route */}
      {isStandalone && (
        <Link to="/markets" className="inline-flex items-center gap-2 text-[#4a4a4a] hover:text-[#1a1a1a] mb-4 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to Public Markets
        </Link>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {activeFilter && (
            <div className="w-10 h-10 rounded-xl bg-[#dae4c4] flex items-center justify-center">
              <AssetIcon className="w-5 h-5 text-[#1a1a1a]" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">
              {activeFilter ? resolveLabel(activeFilter) : 'All Holdings'}
            </h1>
            <p className="text-sm text-[#4a4a4a]">{filteredHoldings.length} holdings</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowValues(!showValues)}
            className="border-[#e5e5e5] text-[#4a4a4a]"
          >
            {showValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-[#e5e5e5] text-[#4a4a4a]"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleAddNew} className="bg-[#ffd87a] hover:bg-[#ffd87a]" data-testid="add-holding-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Holding
          </Button>
        </div>
      </div>

      <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 flex flex-col justify-between">
            <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Current Value</p>
            <p className="text-2xl font-bold text-[#1a1a1a]">{formatCurrency(totalValue)}</p>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 flex flex-col justify-between">
            <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Invested</p>
            <p className="text-2xl font-bold text-[#1a1a1a]">{formatCurrency(totalInvested)}</p>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 flex flex-col justify-between">
            <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Total Gain</p>
            <p className={`text-2xl font-bold ${totalGain >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{formatCurrency(totalGain)}</p>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4 flex flex-col justify-between">
            <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Returns</p>
            <p className={`text-2xl font-bold flex items-center gap-1 ${totalGainPercent >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
              {totalGainPercent >= 0 ? <TrendingUp className="w-5 h-5 flex-shrink-0" /> : <TrendingDown className="w-5 h-5 flex-shrink-0" />}
              {formatPercent(totalGainPercent)}
            </p>
          </div>
        </div>

        {/* Asset class filter pills */}
        {/* {presentAssetClasses.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => handleAssetFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                !activeFilter
                  ? 'bg-[#1a1a1a] text-white shadow-sm'
                  : 'bg-[#f5f5f5] text-[#4a4a4a] hover:bg-[#e5e5e5]'
              }`}
            >
              All
            </button>
            {presentAssetClasses.map(ac => (
              <button
                key={ac}
                onClick={() => handleAssetFilter(ac)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  activeFilter === ac
                    ? 'bg-[#1a1a1a] text-white shadow-sm'
                    : 'bg-[#f5f5f5] text-[#4a4a4a] hover:bg-[#e5e5e5]'
                }`}
              >
                {assetClassMap[ac]?.label || FALLBACK_LABEL_MAP[ac] || ac}
              </button>
            ))}
          </div>
        )} */}

        {/* Search */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by symbol or name..."
              className="pl-10 bg-white border-[#e5e5e5] text-[#1a1a1a]"
            />
          </div>
        </div>

        {/* Holdings Table */}
        <div className="overflow-hidden border border-[#e5e5e5] shadow-sm">
          {filteredHoldings.length === 0 ? (
            <div className="text-center py-12 bg-white">
              <Wallet className="w-12 h-12 text-[#4a4a4a] mx-auto mb-4" />
              <p className="text-[#4a4a4a] mb-4">No holdings found</p>
              <Button onClick={handleAddNew} className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Holding
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#dae4c4]">
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Symbol / Name</th>
                    <th
                      className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider cursor-pointer hover:text-[#4a4a4a]"
                      onClick={() => handleSort('quantity')}
                    >
                      <span className="flex items-center justify-end gap-1">Qty <SortIcon field="quantity" /></span>
                    </th>
                    <th
                      className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider cursor-pointer hover:text-[#4a4a4a]"
                      onClick={() => handleSort('avg_price')}
                    >
                      <span className="flex items-center justify-end gap-1">Avg Price <SortIcon field="avg_price" /></span>
                    </th>
                    <th
                      className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider cursor-pointer hover:text-[#4a4a4a]"
                      onClick={() => handleSort('current_price')}
                    >
                      <span className="flex items-center justify-end gap-1">CMP <SortIcon field="current_price" /></span>
                    </th>
                    <th
                      className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider cursor-pointer hover:text-[#4a4a4a]"
                      onClick={() => handleSort('current_value')}
                    >
                      <span className="flex items-center justify-end gap-1">Value <SortIcon field="current_value" /></span>
                    </th>
                    <th
                      className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider cursor-pointer hover:text-[#4a4a4a]"
                      onClick={() => handleSort('gain_percent')}
                    >
                      <span className="flex items-center justify-end gap-1">P&L <SortIcon field="gain_percent" /></span>
                    </th>
                    <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {filteredHoldings.map((holding, idx) => (
                    <tr
                      key={holding.id}
                      className={`transition-colors hover:bg-[#f9f9f9] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}`}
                      data-testid={`holding-${holding.id}`}
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#dae4c4] flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-[#1a1a1a]">{holding.symbol?.slice(0, 2)}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-[#1a1a1a]">{holding.symbol}</p>
                            <p className="text-xs text-[#4a4a4a]">{holding.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right text-[#2a2a2a] font-medium">
                        {holding.quantity?.toLocaleString('en-IN') || '-'}
                      </td>
                      <td className="px-4 py-3.5 text-right text-[#4a4a4a]">
                        {currSym}{holding.avg_price?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '-'}
                      </td>
                      <td className="px-4 py-3.5 text-right text-[#2a2a2a] font-medium">
                        {currSym}{holding.current_price?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '-'}
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-[#1a1a1a]">
                        {formatCurrency(holding.current_value)}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className={`flex items-center justify-end gap-1 font-semibold ${(holding.gain_percent || 0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                          {(holding.gain_percent || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          <span>{formatPercent(holding.gain_percent)}</span>
                        </div>
                        <p className="text-xs text-[#4a4a4a]">{formatCurrency(holding.gain)}</p>
                      </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(holding)}
                              className="text-[#4a4a4a] hover:text-[#1a1a1a]"
                              data-testid={`edit-${holding.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmId(holding.id)}
                              className="text-[#4a4a4a] hover:text-red-400"
                              data-testid={`delete-${holding.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      {/* Add/Edit Holding Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">
              {isEditing ? 'Edit Holding' : 'Add New Holding'}
            </DialogTitle>
            <DialogDescription>
              Fill in the holding details below
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="portfolio_id">Portfolio *</Label>
              <Select value={currentHolding.portfolio_id} onValueChange={(v) => updateField('portfolio_id', v)}>
                <SelectTrigger className="bg-white border-[#e5e5e5]">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="asset_class">Asset Class *</Label>
              <Select value={currentHolding.asset_class} onValueChange={(v) => updateField('asset_class', v)} disabled={isEditing}>
                <SelectTrigger className="bg-white border-[#e5e5e5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(assetClasses.length ? assetClasses : FALLBACK_ASSET_CLASSES).map(ac => (
                    <SelectItem key={ac.key} value={ac.key}>{ac.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {visibleFields.includes('symbol') && (
              <div>
                <Label htmlFor="symbol">{getLabel(currentHolding.asset_class, 'symbol', 'Symbol')} *</Label>
                <Input
                  id="symbol"
                  value={currentHolding.symbol}
                  onChange={(e) => updateField('symbol', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'symbol', 'RELIANCE, HDFC, etc.')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}

            {visibleFields.includes('name') && (
              <div>
                <Label htmlFor="name">{getLabel(currentHolding.asset_class, 'name', 'Name')} *</Label>
                <Input
                  id="name"
                  value={currentHolding.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'name', 'Full name')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('isin') && (
              <div>
                <Label htmlFor="isin">ISIN</Label>
                <Input
                  id="isin"
                  value={currentHolding.isin}
                  onChange={(e) => updateField('isin', e.target.value.toUpperCase())}
                  placeholder="INE002A01018"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('exchange') && (
              <div>
                <Label htmlFor="exchange">Exchange</Label>
                <Select value={currentHolding.exchange || 'NSE'} onValueChange={(v) => updateField('exchange', v)}>
                  <SelectTrigger className="bg-white border-[#e5e5e5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSE">NSE</SelectItem>
                    <SelectItem value="BSE">BSE</SelectItem>
                    <SelectItem value="MCX">MCX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {visibleFields.includes('quantity') && (
              <div>
                <Label htmlFor="quantity">{getLabel(currentHolding.asset_class, 'quantity', 'Quantity')} *</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={currentHolding.quantity}
                  onChange={(e) => updateField('quantity', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'quantity', '100')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}

            {visibleFields.includes('avg_price') && (
              <div>
                <Label htmlFor="avg_price">{getLabel(currentHolding.asset_class, 'avg_price', `Avg. Buy Price (${currSym})`)} *</Label>
                <Input
                  id="avg_price"
                  type="number"
                  value={currentHolding.avg_price}
                  onChange={(e) => updateField('avg_price', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'avg_price', '2500')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}

            {visibleFields.includes('current_price') && (
              <div>
                <Label htmlFor="current_price">{getLabel(currentHolding.asset_class, 'current_price', `Current Price (${currSym})`)}</Label>
                <Input
                  id="current_price"
                  type="number"
                  value={currentHolding.current_price}
                  onChange={(e) => updateField('current_price', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'current_price', '2800')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}

            {visibleFields.includes('broker') && (
              <div>
                <Label htmlFor="broker">{getLabel(currentHolding.asset_class, 'broker', 'Broker')}</Label>
                <Input
                  id="broker"
                  value={currentHolding.broker}
                  onChange={(e) => updateField('broker', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'broker', 'Zerodha, ICICI Direct, etc.')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('demat_account') && (
              <div>
                <Label htmlFor="demat_account">Demat Account</Label>
                <Input
                  id="demat_account"
                  value={currentHolding.demat_account}
                  onChange={(e) => updateField('demat_account', e.target.value)}
                  placeholder="Demat account number"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('folio_number') && (
              <div>
                <Label htmlFor="folio_number">Folio Number</Label>
                <Input
                  id="folio_number"
                  value={currentHolding.folio_number}
                  onChange={(e) => updateField('folio_number', e.target.value)}
                  placeholder="MF folio number"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('nav') && (
              <div>
                <Label htmlFor="nav">{getLabel(currentHolding.asset_class, 'nav', `NAV (${currSym})`)}</Label>
                <Input
                  id="nav"
                  type="number"
                  value={currentHolding.nav}
                  onChange={(e) => updateField('nav', e.target.value)}
                  placeholder="Current NAV"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('face_value') && (
              <div>
                <Label htmlFor="face_value">Face Value ({currSym})</Label>
                <Input
                  id="face_value"
                  type="number"
                  value={currentHolding.face_value}
                  onChange={(e) => updateField('face_value', e.target.value)}
                  placeholder="1000"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('coupon_rate') && (
              <div>
                <Label htmlFor="coupon_rate">Coupon Rate (%)</Label>
                <Input
                  id="coupon_rate"
                  type="number"
                  step="0.01"
                  value={currentHolding.coupon_rate}
                  onChange={(e) => updateField('coupon_rate', e.target.value)}
                  placeholder="7.5"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('interest_rate') && (
              <div>
                <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                <Input
                  id="interest_rate"
                  type="number"
                  step="0.01"
                  value={currentHolding.interest_rate}
                  onChange={(e) => updateField('interest_rate', e.target.value)}
                  placeholder="7.1"
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('maturity_date') && (
              <div>
                <Label htmlFor="maturity_date">Maturity Date</Label>
                <Input
                  id="maturity_date"
                  type="date"
                  value={currentHolding.maturity_date}
                  onChange={(e) => updateField('maturity_date', e.target.value)}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
            
            {visibleFields.includes('commitment') && (
              <div>
                <Label htmlFor="commitment">{getLabel(currentHolding.asset_class, 'commitment', `Commitment (${currSym})`)}</Label>
                <Input
                  id="commitment"
                  type="number"
                  value={currentHolding.commitment}
                  onChange={(e) => updateField('commitment', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'commitment', 'Total commitment amount')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}

            {visibleFields.includes('called_amount') && (
              <div>
                <Label htmlFor="called_amount">{getLabel(currentHolding.asset_class, 'called_amount', `Called Amount (${currSym})`)}</Label>
                <Input
                  id="called_amount"
                  type="number"
                  value={currentHolding.called_amount}
                  onChange={(e) => updateField('called_amount', e.target.value)}
                  placeholder={getPlaceholder(currentHolding.asset_class, 'called_amount', 'Amount called so far')}
                  className="bg-white border-[#e5e5e5]"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#e5e5e5] text-[#4a4a4a]">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
              {saving ? 'Saving...' : (isEditing ? 'Update Holding' : 'Add Holding')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Delete Holding</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this holding? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="border-[#e5e5e5] text-[#4a4a4a]">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteConfirmId)} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

